import React, { useMemo } from 'react';
import { useSection } from '@/app/providers';
import { SearchableSelect, type SelectOption } from '@/core/ui/components/SearchableSelect';

const ALL_VALUE = '__all__';

interface SectionSelectorProps {
  label?: string;
  className?: string;
  variant?: 'default' | 'glass' | 'slate';
  isSearchable?: boolean;
  isDisabled?: boolean;
  /** Callback adicional cuando cambia la selección (para sincronizar filtros). */
  onSelectionChange?: (sectionId: string | null) => void;
}

/**
 * Selector de sección basado en SectionContext. No se renderiza si el tenant
 * no tiene secciones. null en la selección significa "todas las secciones".
 */
export const SectionSelector: React.FC<SectionSelectorProps> = ({
  label,
  className,
  variant = 'default',
  isSearchable = false,
  isDisabled,
  onSelectionChange,
}) => {
  const { sections, hasSections, isSectionAdmin, selectedSectionId, setSelectedSectionId, loading } =
    useSection();

  const options = useMemo<SelectOption[]>(
    () => [
      { value: ALL_VALUE, label: 'Todas las secciones' },
      ...sections.map((section) => ({ value: section.id, label: section.name })),
    ],
    [sections]
  );

  // El SECTION_ADMIN cambia de sección con el selector global de la barra superior.
  if (!hasSections || isSectionAdmin) return null;

  const handleChange = (value: string | null) => {
    const next = value === ALL_VALUE ? null : value;
    setSelectedSectionId(next);
    onSelectionChange?.(next);
  };

  return (
    <div className={className}>
      <SearchableSelect
        label={label}
        options={options}
        value={selectedSectionId ?? ALL_VALUE}
        onChange={handleChange}
        placeholder="Todas las secciones"
        isClearable={false}
        isSearchable={isSearchable}
        isDisabled={isDisabled || loading}
        variant={variant}
      />
    </div>
  );
};